import type { Project } from '@hangar/shared';
import { stagger } from '@/lib/motion';
import { FormField, fieldA11y } from '@/components/FormField';
import { GatedButton } from '@/components/GatedButton';
import { Icon } from '@/components/Icon';
import { ProblemAlert } from '@/components/ProblemAlert';
import { Card, CardContent, CardDescription, CardFooter, CardHeader, CardTitle } from '@/components/ui/card';
import { Input } from '@/components/ui/input';
import { Textarea } from '@/components/ui/textarea';
import { useGeneralForm } from './useGeneralForm';

type GeneralForm = ReturnType<typeof useGeneralForm>;

/**
 * Design 28 · General: name, the slug (fixed: the CLI and the API use it) and the description (`PATCH /projects/{id}`,
 * If-Match). The design's "Default build provider" is not a project setting in the API (each build profile names its
 * provider account), so it is not shown.
 */
export function GeneralCard({ project, id, index }: { project: Project; id: string; index: number }) {
  const f = useGeneralForm(project);
  return (
    <Card id={id} data-testid="settings-general" className="enter scroll-mt-24" style={stagger(index)}>
      <form onSubmit={(e) => void f.onSubmit(e)} noValidate aria-label="General settings" className="contents">
        <CardHeader>
          <CardTitle>General</CardTitle>
          <CardDescription>Project identity: how it shows in Hangar and in the CLI</CardDescription>
        </CardHeader>
        <CardContent className="flex flex-col gap-4">
          <IdentityFields f={f} slug={project.slug} />
          <FormField id="settings-description" label="Description" error={f.errorFor('description')}>
            <Textarea data-testid="settings-description" value={f.description} maxLength={2000} readOnly={!f.can.allowed} placeholder="What ships from this project" onChange={(e) => f.setDescription(e.target.value)} {...fieldA11y('settings-description', f.errorFor('description'))} />
          </FormField>
          <ProblemAlert error={f.problem} testId="settings-general-problem" />
        </CardContent>
        {f.can.hidden ? null : <GeneralFooter f={f} />}
      </form>
    </Card>
  );
}

function IdentityFields({ f, slug }: { f: GeneralForm; slug: string }) {
  return (
    <div className="grid gap-4 sm:grid-cols-2">
      <FormField id="settings-name" label="Project name" required error={f.errorFor('name')}>
        <Input data-testid="settings-name" value={f.name} maxLength={120} autoComplete="off" readOnly={!f.can.allowed} onChange={(e) => f.setName(e.target.value)} {...fieldA11y('settings-name', f.errorFor('name'))} />
      </FormField>
      <FormField id="settings-slug" label="Project slug" hint="Fixed: the CLI, the API and pipelines use it" trailing={<Icon name="lock" size={14} label="Locked" className="text-subtle" />}>
        <div className="relative">
          <Input id="settings-slug" data-testid="settings-slug" value={slug} readOnly aria-readonly className="cursor-not-allowed bg-secondary pr-9 font-mono text-muted-foreground sm:text-[13px]" aria-describedby="settings-slug-hint" />
          <Icon name="lock" size={16} className="pointer-events-none absolute right-3 top-1/2 -translate-y-1/2 text-subtle" />
        </div>
      </FormField>
    </div>
  );
}

function GeneralFooter({ f }: { f: GeneralForm }) {
  return (
    <CardFooter className="justify-end gap-3">
      {f.dirty ? <span className="text-xs text-subtle">Unsaved changes</span> : null}
      <GatedButton type="submit" variant="outline" data-testid="settings-general-save" can={f.can} disabled={!f.dirty} loading={f.isPending}>
        Save
      </GatedButton>
    </CardFooter>
  );
}
